import L from 'leaflet';
import { HOUSE_DOCUMENT } from '../constants/documentTheme';
import { OSM_ATTRIBUTION, OSM_TILE_URL } from './renderRoadTripLeafletMap';

export const SUGGESTION_POI_COLORS = {
  hotels: HOUSE_DOCUMENT.oxblood,
  restaurants: HOUSE_DOCUMENT.accent,
  locations: '#1A0E12',
  activities: '#1A0E12'
};

export const SUGGESTION_POI_LETTERS = {
  hotels: 'H',
  restaurants: 'R',
  locations: 'P',
  activities: 'A'
};

export const SUGGESTION_POI_HOT = '#E11D48';

export function suggestionPoiDivIcon(poi = {}, { highlighted = false } = {}) {
  if (highlighted) {
    return L.divIcon({
      className: 'ai-suggestion-poi-marker ai-suggestion-poi-hot',
      html: `<svg width="28" height="42" viewBox="0 0 24 36" aria-hidden="true">
        <path d="M12 1C6.5 1 2 5.6 2 11.3 2 20 12 35 12 35s10-15 10-23.7C22 5.6 17.5 1 12 1z"
          fill="${SUGGESTION_POI_HOT}" stroke="#fff" stroke-width="1.6"/>
        <circle cx="12" cy="11.2" r="4.1" fill="#fff"/>
      </svg>`,
      iconSize: [28, 42],
      iconAnchor: [14, 40],
      popupAnchor: [0, -36]
    });
  }

  const color = SUGGESTION_POI_COLORS[poi.category] || HOUSE_DOCUMENT.oxblood;
  const letter = SUGGESTION_POI_LETTERS[poi.category] || 'P';
  return L.divIcon({
    className: 'ai-suggestion-poi-marker',
    html: `<div style="
      width: 26px; height: 26px; border-radius: 50%;
      background-color: ${color}; color: #F6EFE4;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 11px; font-family: sans-serif;
      border: 2px solid #fff;
      box-shadow: 0 2px 5px rgba(0,0,0,0.28);
    ">${letter}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -14]
  });
}

export function highlightSuggestionPoi(markers = {}, key) {
  Object.entries(markers || {}).forEach(([markerKey, marker]) => {
    const poi = marker?._suggestionPoi;
    if (!poi) return;
    const highlighted = Boolean(key) && markerKey === key;
    marker.setIcon?.(suggestionPoiDivIcon(poi, { highlighted }));
    marker.setZIndexOffset?.(highlighted ? 800 : 0);
  });
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function renderSuggestionPoisOnMap(map, pois = [], { fallbackCenter } = {}) {
  if (!map) return { bounds: null, markers: {} };

  const tiles = L.tileLayer(OSM_TILE_URL, {
    attribution: OSM_ATTRIBUTION,
    maxZoom: 19
  });
  tiles?.addTo?.(map);

  const markers = {};
  let bounds = null;
  pois.forEach((poi) => {
    if (poi.lat == null || poi.lng == null) return;
    const latlng = [poi.lat, poi.lng];
    if (!bounds) {
      bounds = L.latLngBounds(latlng, latlng);
    } else {
      bounds.extend(latlng);
    }
    const marker = L.marker(latlng, {
      icon: suggestionPoiDivIcon(poi),
      title: poi.name
    });
    marker._suggestionPoi = poi;
    const details = [
      poi.city && escapeHtml(poi.city),
      poi.rating && escapeHtml(poi.rating)
    ].filter(Boolean).join(' · ');
    marker?.bindPopup?.(`<strong>${escapeHtml(poi.name)}</strong>${details ? `<br/><small>${details}</small>` : ''}`);
    marker?.addTo?.(map);
    if (poi.key) markers[poi.key] = marker;
  });

  if (bounds?.isValid?.()) {
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 14, animate: false });
  } else if (fallbackCenter?.lat != null && fallbackCenter?.lng != null) {
    map.setView?.([fallbackCenter.lat, fallbackCenter.lng], 11);
  }
  return { bounds, markers };
}
